// Consult REDESIGN_TOKENS.md (this directory) before changing any variant,
// palette token, or component override here.

import {
  createTheme,
  responsiveFontSizes,
  type ThemeOptions,
} from '@mui/material/styles'
import { outlinedInputClasses } from '@mui/material/OutlinedInput'
import { ArrowDownwardIcon } from 'icons'
import { colors } from './colors'
import { palette } from './palette'
import { typography } from './typography'
import { shadows } from './shadows'

// `variant="secondary"` — a tinted button (primary.light fill, primary text) for low-emphasis CTAs.
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    secondary: true
  }
}

// `surface` says where a tab row sits, which decides whether it draws a divider:
// tabs inside a card do, tabs sitting directly on the page don't (ENG-1224).
// A new prop rather than `variant`, whose union MUI closes and AssessmentMap
// already uses functionally (`variant="scrollable"`).
declare module '@mui/material/Tabs' {
  interface TabsOwnProps {
    surface?: 'card' | 'page'
  }
}

const themeOptions: ThemeOptions = {
  components: {
    // Slim scrollbars (ENG-1135).
    MuiCssBaseline: {
      styleOverrides: {
        '*': {
          '&::-webkit-scrollbar': {
            width: 8,
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            borderRadius: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.3)',
          },
          scrollbarWidth: 'thin',
          scrollbarColor: 'rgba(0, 0, 0, 0.2) transparent',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          transition:
            'background-color 300ms, color 300ms, border-color 300ms, transform 150ms',
          '&:hover': {
            transform: 'translateY(-1px)',
          },
          '&.MuiButton-loading.Mui-disabled': {
            color: 'transparent',
          },
          '&.MuiButton-containedPrimary .MuiButton-loadingWrapper svg': {
            color: 'white',
          },
          '&.MuiButton-outlined': {
            background: 'white',
            boxShadow: shadows[3],
          },
          '&.MuiButton-containedSecondary': {
            border: '1px solid rgba(0, 0, 0, 0.12)',
          },
          '&.MuiButton-outlinedPrimary': {
            background: 'white',
            boxShadow: shadows[2],
          },
        },
      },
      variants: [
        // In `variants` so it lands AFTER MUI's internal containedPrimary variant
        // in source order — otherwise MUI's default primary.dark hover wins.
        {
          props: { variant: 'contained', color: 'primary' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.mainHover,
            },
          }),
        },
        {
          props: { variant: 'secondary' },
          style: ({ theme }) => ({
            backgroundColor: theme.palette.primary.light,
            color: theme.palette.primary.contrastText,
            padding: '6px 16px',
            '&:hover': {
              backgroundColor: theme.palette.primary.lightHover,
            },
          }),
        },
        // Custom variants don't inherit MUI's per-size padding (those rules
        // target text/outlined/contained variants). Mirror the contained
        // paddings explicitly so `size="small"` actually shrinks the button.
        {
          props: { variant: 'secondary', size: 'small' },
          style: { padding: '4px 10px' },
        },
        {
          props: { variant: 'secondary', size: 'large' },
          style: { padding: '8px 22px' },
        },
      ],
    },
    MuiSwitch: {
      styleOverrides: {
        root: {
          knobFillEnabled: '#FAFAFA',
          slideFill: '#000000',
          knowFillDisabled: '#F5F5F5',
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 4,
          ...typography.h5,
          '&.MuiChip-outlined': {
            boxShadow: shadows[4],
          },
          '&.MuiChip-outlinedDefault': {
            borderColor: palette.text?.primary,
            background: palette.background?.default,
          },
        },
      },
      // MUI draws coloured outlined borders at 70% opacity, which drops info below the
      // 3:1 boundary minimum; the full colour clears it (ENG-1320).
      variants: (
        ['primary', 'info', 'success', 'warning', 'error'] as const
      ).map((color) => ({
        props: { variant: 'outlined', color },
        style: ({ theme }) => ({ borderColor: theme.palette[color].main }),
      })),
    },
    MuiLink: {
      styleOverrides: {
        root: ({ theme }) => ({
          '&:hover': {
            color: theme.palette.primary.dark,
            textDecorationColor: theme.palette.primary.dark,
          },
        }),
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // The border only clears 3:1 against white, so every field paints its own white interior
        // rather than inheriting a tinted pane. MUI's hover/focus/error/disabled borders are more
        // specific and still win; its touch-device sticky-hover reset is not, hence the media rule.
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          [`& .${outlinedInputClasses.notchedOutline}`]: {
            borderColor: theme.palette.field.border,
          },
          '@media (hover: none)': {
            [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
              borderColor: theme.palette.field.border,
            },
          },
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        // MUI fades the placeholder to 42% of text.primary (below 4.5:1) and sets it per vendor
        // selector, so each one needs overriding.
        input: ({ theme }) => {
          const placeholder = { color: theme.palette.text.disabled, opacity: 1 }
          return {
            '&::placeholder': placeholder,
            '&::-webkit-input-placeholder': placeholder,
            '&::-moz-placeholder': placeholder,
            '&::-ms-input-placeholder': placeholder,
          }
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          padding: 3,
          borderWidth: 1,
          borderStyle: 'solid',
          borderColor: palette.divider,
          boxShadow: shadows[3],
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          padding: 2,
          boxShadow: shadows[9],
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: '#2A2A2A',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          borderColor: colors.redesignNeutral.border,
          borderWidth: 1,
          borderStyle: 'solid',
          overflow: 'hidden',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          paddingLeft: 16,
          paddingRight: 16,
          borderBottomColor: colors.redesignNeutral.border,
          borderBottomWidth: 1,
          borderBottomStyle: 'solid',
        },
        head: {
          // Neutral chrome surface (not the teal tint) so the header reads as chrome against the
          // white table body. Solid, not translucent, so body rows can't bleed through a sticky header.
          backgroundColor: colors.redesignNeutral.chromeSurface,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontSize: '0.6875rem',
          fontWeight: 600,
          // Tight enough that a title wrapped to two lines reads as one header, not two spaced rows.
          lineHeight: 1.2,
          color: palette.text?.secondary,
          // Top-align so every title starts at the same edge whether it wraps to one line or
          // two (ENG-1194/ENG-1196 header treatment).
          verticalAlign: 'top',
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          '& td, & th': {
            borderBottomColor: colors.redesignNeutral.border,
            borderBottomWidth: 1,
            borderBottomStyle: 'solid',
          },
          '& tr:last-of-type td': {
            borderBottom: 'none',
          },
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          backgroundColor: 'white',
          // Divider between the table body and the pagination so the two read as one card. The last
          // body row drops its own bottom border (MuiTable above), so this is the single separator.
          borderTop: `1px solid ${colors.redesignNeutral.border}`,
        },
        // Align the controls with the 16px cell padding so the card's right edge is consistent, and
        // tighten the default 52px toolbar so the pagination row isn't taller than the data rows.
        toolbar: {
          paddingTop: 0,
          paddingBottom: 0,
          paddingRight: 16,
        },
      },
    },
    MuiTableSortLabel: {
      defaultProps: {
        // One directional arrow for idle and active. MUI rotates it by the `direction` prop
        // (down = desc, rotated up = asc), so at rest it previews the sort the first click
        // applies — call sites pass their first-click direction when inactive.
        IconComponent: ArrowDownwardIcon,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          // Keep the sort arrow to the right of the label everywhere. MUI defaults to
          // flex-direction: inherit, so right-aligned cells (row-reverse) would otherwise flip the
          // arrow to the left.
          flexDirection: 'row',
          // Header cells are top-aligned and long titles wrap; anchor the arrow beside the
          // first line instead of centering it against the whole wrapped block.
          alignItems: 'flex-start',
          // Keep the arrow faintly visible on every sortable header at rest (MUI hides its icon
          // until active), so a column reads as sortable before it's clicked.
          '& .MuiTableSortLabel-icon': {
            opacity: 0.4,
            fontSize: '0.875rem', // 14px — proportioned to the 11px header type (MUI default is 18px)
          },
          '&.Mui-active': {
            color: theme.palette.primary.main,
            fontWeight: 700,
            '& .MuiTableSortLabel-icon': {
              color: theme.palette.primary.main,
              opacity: 1,
            },
          },
        }),
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          // Compact, chip-like toggle (promoted from the Analytics Chart/Table toggle): bordered and
          // bold, padded just enough to hug its label, filling to primary when selected.
          border: `1px solid ${theme.palette.divider}`,
          fontWeight: 700,
          textTransform: 'none',
          lineHeight: 1.2,
          minHeight: 0,
          paddingLeft: 10,
          paddingRight: 10,
          paddingTop: 2,
          paddingBottom: 2,
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.paper,
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
        }),
      },
    },
    MuiButtonGroup: {
      styleOverrides: {
        root: {
          boxShadow: shadows[3],
          background: 'white',
        },
      },
    },
    // Underline tabs: the selected tab's 2px highlight is MUI's own indicator.
    // The divider under the row is opt-in via `surface="card"` below.
    MuiTabs: {
      styleOverrides: {
        root: {
          // Collapse MUI's default 48px strip so the row hugs its labels;
          // per-Tab minHeight (e.g. AssessmentMap's icon tabs) still wins.
          minHeight: 0,
        },
        // 24px between adjacent labels, carried by the row's gap rather than
        // per-tab padding: padding would also indent the first label and pad the
        // last, pushing the row out of line with the content under it and
        // costing the 340px rail 24px of width it needs to fit three labels.
        flexContainer: {
          gap: 24,
        },
        indicator: {
          backgroundColor: palette.text?.primary,
        },
      },
      variants: [
        {
          props: { surface: 'card' },
          style: ({ theme }) => ({
            borderBottom: `1px solid ${theme.palette.divider}`,
          }),
        },
      ],
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontSize: typography.body2?.fontSize,
          fontWeight: 500,
          minWidth: 0,
          minHeight: 0,
          // Horizontal spacing lives in the row's gap above, so the first and
          // last tabs stay flush with the surrounding content.
          padding: '8px 0',
          color: palette.text?.secondary,
          transition: 'color 0.15s',
          '&.Mui-selected': {
            color: palette.text?.primary,
            fontWeight: 700,
          },
        },
      },
    },
    // Determinate bar for the analytics tables: primary fill (MUI's default for
    // color="primary") on a neutral grey track, rounded on both ends. Not the
    // reusable teal→green ProgressBar, which reads as too much in dense rows.
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 6,
          borderRadius: 4,
          backgroundColor: colors.redesignNeutral.border,
        },
        bar: ({ theme }) => ({
          borderRadius: 4,
          backgroundColor: theme.palette.primary.main,
        }),
      },
    },
  },
  palette,
  mixins: {
    toolbar: {
      minHeight: '48px',
      padding: '16px',
      '@media (min-width: 600px)': {
        minHeight: '48px',
      },
    },
  },
  shadows,
  shape: {
    borderRadius: 8,
  },
  typography,
}

const theme = responsiveFontSizes(createTheme(themeOptions))

export { theme }
